import { MutableRefObject, useLayoutEffect, useRef, useState } from 'react';
import { PILL_COLUMN_GAP, PILL_COMPACT_PADDING_X } from './rachelInput.styles';

const SINGLE_LINE_MAX_HEIGHT = 24;

export interface UseRachelInputMultilineParams {
  inputRef: MutableRefObject<HTMLInputElement | null>;
  value: string;
}

interface LineMeasurement {
  input: HTMLInputElement;
  measurer: HTMLDivElement;
}

const MEASURER_STYLES = {
  position: 'absolute',
  top: '0',
  left: '0',
  visibility: 'hidden',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
} as const;

const getPill = (input: HTMLInputElement) => input.parentElement?.parentElement ?? null;

const getCompactInputWidth = (input: HTMLInputElement) => {
  const pill = getPill(input);
  const spark = pill?.firstElementChild;
  const actions = pill?.lastElementChild;

  if (!pill || !spark || !actions) {
    return null;
  }

  const chromeWidth =
    spark.clientWidth + actions.clientWidth + PILL_COLUMN_GAP * 2 + PILL_COMPACT_PADDING_X * 2;

  return pill.clientWidth - chromeWidth;
};

const createMeasurer = (input: HTMLInputElement) => {
  const measurer = document.createElement('div');

  measurer.setAttribute('aria-hidden', 'true');
  Object.assign(measurer.style, MEASURER_STYLES);
  input.parentElement?.append(measurer);

  return measurer;
};

const getMultilineState = (measurement: LineMeasurement | null, text: string) => {
  if (!measurement) {
    return null;
  }

  if (!text.trim()) {
    return false;
  }

  const compactWidth = getCompactInputWidth(measurement.input);
  if (!compactWidth || compactWidth <= 0) {
    return null;
  }

  measurement.measurer.style.width = `${compactWidth}px`;
  measurement.measurer.textContent = text;

  return measurement.measurer.scrollHeight > SINGLE_LINE_MAX_HEIGHT;
};

export const useRachelInputMultiline = ({ inputRef, value }: UseRachelInputMultilineParams) => {
  const [isMultiline, setIsMultiline] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);
  const measurementRef = useRef<LineMeasurement | null>(null);
  const valueRef = useRef(value);

  const applyMultilineState = (text: string) => {
    const nextIsMultiline = getMultilineState(measurementRef.current, text);

    if (nextIsMultiline !== null) {
      setIsMultiline(nextIsMultiline);
    }
  };

  const measureRef = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    observerRef.current?.disconnect();
    measurementRef.current?.measurer.remove();
    observerRef.current = null;
    measurementRef.current = null;

    if (!node) {
      return;
    }

    measurementRef.current = { input: node, measurer: createMeasurer(node) };

    const observer = new ResizeObserver(() => applyMultilineState(valueRef.current));
    observer.observe(getPill(node) ?? node);
    observerRef.current = observer;

    applyMultilineState(valueRef.current);
  };

  useLayoutEffect(() => {
    valueRef.current = value;
    const nextIsMultiline = getMultilineState(measurementRef.current, value);

    if (nextIsMultiline !== null) {
      setIsMultiline(nextIsMultiline);
    }
  }, [value]);

  return { isMultiline, measureRef };
};
